import type { FC, ReactNode } from 'react'

import { Award } from 'lucide-react'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { FinalCta } from '@/components/kit/final-cta'
import { Marquee } from '@/components/kit/marquee'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { Spark } from '@/components/kit/spark'
import { SplitText } from '@/components/kit/split-text'
import { Collage } from '@/components/about/collage'
import { TeamGrid } from '@/components/about/team-grid'
import { Timeline } from '@/components/about/timeline'
import { recognition, tools, values } from '@/constants/about'

const Studio: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Studio'
                description="Meet Updraft, a London social media studio of forecasters, editors and creators who read the feed so brands can be talked about."
            />

            <section
                aria-labelledby='about-title'
                className='relative overflow-hidden pb-10 pt-36 md:pt-44'
            >
                <div
                    aria-hidden='true'
                    className='bg-grid mask-radial absolute inset-0'
                />

                <div className='relative mx-auto max-w-[1400px] px-5 md:px-10'>
                    <SectionLabel>Studio · who reads the weather</SectionLabel>
                    <SplitText
                        as='h1'
                        id='about-title'
                        lines={['Small studio,', 'big weather']}
                        italic='big'
                        highlight='weather'
                        className='mt-6 text-[clamp(3.25rem,10vw,9.25rem)] font-bold leading-[0.86] tracking-[-0.045em]'
                    />
                    <Reveal
                        as='p'
                        delay={300}
                        className='mt-10 max-w-xl text-xl leading-relaxed text-stone'
                    >
                        Seventeen forecasters, editors, producers and number people in a converted print shop in Shoreditch. We read the feed all day so you do not have to.
                    </Reveal>
                </div>

                <div className='mt-16 px-5 md:mt-20 md:px-10'>
                    <Collage />
                </div>
            </section>

            <section
                id='story'
                aria-labelledby='story-title'
                className='mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28'
            >
                <div className='grid gap-10 lg:grid-cols-12'>
                    <div className='lg:col-span-5'>
                        <SectionLabel index='01'>The story so far</SectionLabel>
                        <SplitText
                            as='h2'
                            id='story-title'
                            lines={['It started with', 'a weather report']}
                            italic='weather'
                            className='mt-5 text-[clamp(2.4rem,4.6vw,4.25rem)] font-bold leading-[0.92] tracking-[-0.045em]'
                        />
                    </div>

                    <Reveal
                        delay={150}
                        className='flex flex-col gap-5 text-lg leading-relaxed text-ink/80 lg:col-span-6 lg:col-start-7'
                    >
                        <p className='first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:font-heading first-letter:text-[4.5rem] first-letter:font-bold first-letter:leading-[0.8] first-letter:text-ink'>
                            In 2019 Maya Okafor was buying ads for big brands and noticing something odd. The ads that worked best were never the expensive ones. They were the ones that felt like they had read the room.
                        </p>
                        <p>
                            So she started writing forecasts. One page, every week, about what people online were about to care about. Clients read them, then asked her to make the things she was predicting. Updraft grew out of that kitchen table habit: forecast first, then make, then read what happened.
                        </p>
                        <p>
                            Today we are seventeen people in London with a small outpost in Lisbon, working with pickle bars, earbud makers, potters and run clubs. The habit has not changed. We still write the forecast before anyone touches a camera.
                        </p>
                    </Reveal>
                </div>

                <Reveal className='mt-20 border-y border-ink/10 py-14 md:mt-28 md:py-20'>
                    <figure className='mx-auto max-w-5xl text-center'>
                        <Spark className='mx-auto size-7 text-ember' />
                        <blockquote className='mt-8 font-heading text-[clamp(2rem,5vw,4.5rem)] font-bold leading-[1.04] tracking-[-0.035em]'>
                            <p>“The algorithm is just a very large crowd. Talk to one person in it and the rest start listening.”</p>
                        </blockquote>
                        <figcaption className='mt-8 font-mono text-xs uppercase tracking-[0.16em] text-stone'>
                            Maya Okafor, founder
                        </figcaption>
                    </figure>
                </Reveal>
            </section>

            <section
                id='values'
                aria-labelledby='values-title'
                className='bg-sand py-20 md:py-28'
            >
                <div className='mx-auto max-w-[1400px] px-5 md:px-10'>
                    <div className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                        <div>
                            <SectionLabel index='02'>What we stand for</SectionLabel>
                            <SplitText
                                as='h2'
                                id='values-title'
                                lines={['Four words on', 'the studio wall']}
                                italic='wall'
                                className='mt-5 text-[clamp(2.4rem,4.6vw,4.25rem)] font-bold leading-[0.92] tracking-[-0.045em]'
                            />
                        </div>
                        <Reveal
                            as='p'
                            delay={150}
                            className='max-w-xs text-base text-stone'
                        >
                            They are painted above the coffee machine. Hover one to see what it means on a Tuesday.
                        </Reveal>
                    </div>

                    <ul className='mt-12 border-t border-ink/10'>
                        {values.map((value, index) => (
                            <Reveal
                                key={value.word}
                                as='li'
                                delay={index * 80}
                            >
                                <div className='group grid items-center gap-3 border-b border-ink/10 py-6 md:grid-cols-12 md:gap-8 md:py-8'>
                                    <span className='font-mono text-xs text-stone md:col-span-1'>
                                        0{index + 1}
                                    </span>
                                    <h3 className='fill-text font-heading text-[clamp(4rem,13vw,11rem)] font-bold leading-[0.84] tracking-[-0.045em] md:col-span-7'>
                                        {value.word}
                                    </h3>
                                    <p className='text-lg leading-snug text-ink transition-[opacity,translate] duration-700 ease-expo md:col-span-4 md:translate-y-3 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100'>
                                        {value.line}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <section
                id='team'
                aria-labelledby='team-title'
                className='mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28'
            >
                <div className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                    <div>
                        <SectionLabel index='03'>The crew</SectionLabel>
                        <SplitText
                            as='h2'
                            id='team-title'
                            lines={['Faces you will', 'meet on day one']}
                            italic='day'
                            className='mt-5 text-[clamp(2.4rem,4.6vw,4.25rem)] font-bold leading-[0.92] tracking-[-0.045em]'
                        />
                    </div>
                    <Reveal
                        as='p'
                        delay={150}
                        className='max-w-sm text-base leading-relaxed text-stone'
                    >
                        Hover or tap a portrait for the thing they would never put on a CV.
                    </Reveal>
                </div>

                <div className='mt-12'>
                    <TeamGrid />
                </div>
            </section>

            <section
                id='timeline'
                aria-labelledby='timeline-title'
                className='bg-paper py-20 md:py-28'
            >
                <div className='mx-auto max-w-[1100px] px-5 md:px-10'>
                    <div className='text-center'>
                        <SectionLabel
                            index='04'
                            className='justify-center'
                        >
                            Milestones
                        </SectionLabel>
                        <SplitText
                            as='h2'
                            id='timeline-title'
                            lines={['From a kitchen table', 'to a print shop']}
                            italic='kitchen'
                            className='mt-5 text-[clamp(2.4rem,4.6vw,4.25rem)] font-bold leading-[0.92] tracking-[-0.045em]'
                        />
                    </div>

                    <div className='mt-16 md:mt-20'>
                        <Timeline />
                    </div>
                </div>
            </section>

            <section
                aria-labelledby='tools-title'
                className='overflow-hidden py-20 md:py-24'
            >
                <div className='mx-auto max-w-[1400px] px-5 md:px-10'>
                    <SectionLabel index='05'>The kit</SectionLabel>
                    <h2
                        id='tools-title'
                        className='mt-5 text-[clamp(2rem,3.6vw,3.25rem)] font-bold tracking-[-0.04em]'
                    >
                        Tools we <span className='font-serif font-normal italic tracking-[-0.01em]'>love</span>, and a few we cannot explain
                    </h2>
                </div>

                <Marquee
                    speed={45}
                    className='mt-10'
                >
                    {tools.map(tool => (
                        <span
                            key={tool}
                            className='mr-3 inline-flex items-center gap-3 rounded-full border border-ink/10 bg-paper px-6 py-3.5 font-heading text-xl font-bold tracking-[-0.03em] md:text-2xl'
                        >
                            <Spark className='size-3 text-ember' />
                            {tool}
                        </span>
                    ))}
                </Marquee>

                <div className='mx-auto mt-16 max-w-[1400px] px-5 md:px-10'>
                    <h3 className='font-mono text-[11px] font-normal uppercase tracking-[0.16em] text-stone'>
                        Nice things people printed
                    </h3>
                    <ul className='mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
                        {recognition.map((item, index) => (
                            <Reveal
                                key={item.title}
                                as='li'
                                delay={index * 80}
                            >
                                <div className='flex h-full flex-col justify-between gap-8 rounded-[1.5rem] border border-black/5 bg-paper p-6'>
                                    <Award
                                        aria-hidden='true'
                                        strokeWidth={1.3}
                                        className='size-8 text-ink'
                                    />
                                    <div>
                                        <p className='font-heading text-xl font-bold leading-tight tracking-[-0.035em]'>{item.title}</p>
                                        <p className='mt-2 font-mono text-[11px] uppercase tracking-[0.12em] text-stone'>{item.source}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <FinalCta
                lines={['Come and say hello', 'to the crew']}
                italic='hello'
                body='Tell us about your brand. We reply within 24 hours with honest thoughts and a free forecast, usually with coffee in hand.'
            />
        </Layout>
    )
}

export default Studio
